import Modal from '@advanza/modal'
import { Button, Divider, PreIcon } from '@advanza/ui'
import DatePicker from 'components/DatePicker'
import AutoRow from 'components/misc/AutoRow'
import { useState } from 'react'

const ExportFeedbackButton = () => {
    const [openModal, setOpenModal] = useState(false)
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [dateError, setDateError] = useState('')

    const setDateRange = (days: number) => {
        const end = new Date()
        const start = new Date()
        start.setDate(start.getDate() - days)

        setEndDate(end.toISOString().split('T')[0])
        setStartDate(start.toISOString().split('T')[0])
        setDateError('')
    }

    const onDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        if (name === 'startDate') {
            setStartDate(value)
            // Validate: if endDate is set and new startDate is after endDate, show error
            if (endDate && value && new Date(value) > new Date(endDate)) {
                setDateError('Start date cannot be after end date')
            } else {
                setDateError('')
            }
        } else if (name === 'endDate') {
            setEndDate(value)
            // Validate: if startDate is set and new endDate is before startDate, show error
            if (startDate && value && new Date(value) < new Date(startDate)) {
                setDateError('End date cannot be before start date')
            } else {
                setDateError('')
            }
        }
    }

    const handleExport = () => {
        if (!startDate || !endDate) {
            return
        }

        // Validate date range
        if (new Date(startDate) > new Date(endDate)) {
            setDateError('Start date cannot be after end date')
            return
        }

        const { protocol, hostname } = window.location
        const params = new URLSearchParams({
            startDate,
            endDate,
            format: 'excel',
        })

        const exportUrl = `${protocol}//${hostname}/api/office/misc/export-feedback?${params.toString()}`
        window.open(exportUrl, '_blank', 'noopener,noreferrer')
        setOpenModal(false)
    }

    // Calculate disabled dates for DatePickers using functions
    const getStartDateDisabled = () => {
        const today = new Date()
        today.setHours(23, 59, 59, 999)
        const maxDate = endDate ? new Date(endDate) : today
        maxDate.setHours(23, 59, 59, 999)

        return (date: Date) => {
            // Disable dates after today or after endDate (if set)
            return date > maxDate
        }
    }

    const getEndDateDisabled = () => {
        const today = new Date()
        today.setHours(23, 59, 59, 999)
        const minDate = startDate ? new Date(startDate) : null

        return (date: Date) => {
            // Disable dates after today
            if (date > today) {
                return true
            }
            // Disable dates before startDate (if set)
            if (minDate) {
                const start = new Date(minDate)
                start.setHours(0, 0, 0, 0)
                return date < start
            }
            return false
        }
    }

    return (
        <div>
            <AutoRow>
                <Button onClick={() => setOpenModal(true)}>
                    <PreIcon icon="download">Export Feedback</PreIcon>
                </Button>
            </AutoRow>
            <Modal close={() => setOpenModal(false)} open={openModal} zIndex={102}>
                <h3>Export Feedback</h3>
                <div className="space-y-3">
                    {/* Quick date range buttons */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary-700 mb-1.5">
                            Quick Select
                        </label>
                        <div className="flex gap-1.5 flex-wrap">
                            <button
                                type="button"
                                onClick={() => setDateRange(7)}
                                className="px-2.5 py-1 text-sm font-medium text-text-secondary-700 bg-grey-200 border border-grey-500 rounded hover:bg-grey-500 transition-colors">
                                Last 1 Week
                            </button>
                            <button
                                type="button"
                                onClick={() => setDateRange(30)}
                                className="px-2.5 py-1 text-sm font-medium text-text-secondary-700 bg-grey-200 border border-grey-500 rounded hover:bg-grey-500 transition-colors">
                                Last 1 Month
                            </button>
                            <button
                                type="button"
                                onClick={() => setDateRange(90)}
                                className="px-2.5 py-1 text-sm font-medium text-text-secondary-700 bg-grey-200 border border-grey-500 rounded hover:bg-grey-500 transition-colors">
                                Last 3 Months
                            </button>
                        </div>
                    </div>

                    {/* Date inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <DatePicker
                                legend
                                placeholder="Start Date"
                                onChange={onDateChange}
                                dayPickerProps={{
                                    disabled: getStartDateDisabled(),
                                    showOutsideDays: false,
                                }}
                                value={startDate}
                                name="startDate"
                            />
                        </div>
                        <div>
                            <DatePicker
                                legend
                                placeholder="End Date"
                                onChange={onDateChange}
                                dayPickerProps={{
                                    disabled: getEndDateDisabled(),
                                    showOutsideDays: false,
                                }}
                                value={endDate}
                                name="endDate"
                            />
                        </div>
                    </div>
                    {dateError && <div className="text-sm text-error mt-1">{dateError}</div>}
                </div>
                <Divider xxxl />
                <AutoRow>
                    <Button disabled={!startDate || !endDate || !!dateError} onClick={handleExport}>
                        <PreIcon primColor icon="download">
                            Export Feedback
                        </PreIcon>
                    </Button>
                </AutoRow>
            </Modal>
        </div>
    )
}

export default ExportFeedbackButton
